import { useEffect, useMemo, useState } from 'react';
import { Alert, Form, Input, InputNumber, Select, message } from 'antd';
import { DraggableModal } from '../../components/DraggableModal';
import {
  billingApi,
  maskOrderMobile,
} from '../../api/billing.api';
import {
  productApi,
  type ProductItem,
} from '../../api/products.api';
import {
  studentsApi,
  type StudentListItem,
} from '../../api/students.api';
import { orgCampusApi, type CampusItem } from '../../api/campuses.api';
import { MoneyDisplay } from '../../components/MoneyDisplay';
import { useHasPermission } from '../../auth/HasPermission';

interface OrderCreateFormValues {
  studentId: string;
  productId: string;
  /** 减免金额（元，两位小数；仅 DISCOUNT 授权可见，manual-order-discount）。 */
  discountAmountYuan?: number;
  /** 减免原因（减免大于 0 时必填，≤200 字）。 */
  discountReason?: string;
}

export interface OrderCreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/** 管理端直建订单对话框。 */
export function OrderCreateModal({ open, onClose, onCreated }: OrderCreateModalProps) {
  const [form] = Form.useForm<OrderCreateFormValues>();
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [loading, setLoading] = useState(false);
  const studentId = Form.useWatch('studentId', form);
  const productId = Form.useWatch('productId', form);
  const canDiscount = useHasPermission('ADM-09', 'DISCOUNT');
  const discountYuan = Form.useWatch('discountAmountYuan', form);

  const selectedStudent = useMemo(
    () => students.find((student) => student.id === studentId),
    [studentId, students],
  );
  const selectedProduct = useMemo(
    () => products.find((product) => product.id === productId),
    [productId, products],
  );
  // manual-order-discount SC-05：应收联动 = 定价 − 减免；未授权/未填减免时恒为定价；
  // 元转分用整数安全转换（Math.round 抹平浮点误差，19.9 元 → 1990 分，SC-06）
  const discountMinor = canDiscount ? Math.round((discountYuan ?? 0) * 100) : 0;
  const receivableMinor = selectedProduct
    ? Math.max(0, selectedProduct.price - discountMinor)
    : 0;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setStudents([]);
    setProducts([]);
    Promise.all([
      studentsApi.listStudents({ page: 1, pageSize: 100 }),
      productApi.listProducts({ status: 'ON_SHELF', page: 1, pageSize: 100 }),
      orgCampusApi.listCampuses({ status: 'ACTIVE', page: 1, pageSize: 100 }),
    ])
      .then(([studentResult, productResult, campusResult]) => {
        setStudents(studentResult.items);
        setProducts(productResult.items);
        setCampuses(campusResult.items);
      })
      .catch((err: unknown) => {
        message.error(err instanceof Error ? err.message : '基础数据加载失败');
      });
  }, [form, open]);

  const handleOk = async () => {
    let values: OrderCreateFormValues;
    try {
      values = await form.validateFields();
    } catch {
      // 表单校验失败：错误已内联展示，不弹「创建订单失败」
      return;
    }
    if (!selectedStudent) {
      message.error('请选择学生');
      return;
    }
    setLoading(true);
    try {
      // manual-order-discount SC-02：未授权会话不携带减免字段；服务端 DISCOUNT 校验为最终防线
      const result = await billingApi.createDirectOrder({
        student: {
          studentId: selectedStudent.id,
          campusId: selectedStudent.homeCampusId,
        },
        productId: values.productId,
        ...(canDiscount
          ? {
              discountAmount: discountMinor,
              discountReason:
                discountMinor > 0 ? (values.discountReason ?? '').trim() : null,
            }
          : {}),
      });
      message.success(`订单 ${result.orderNo} 已创建`);
      onCreated();
      onClose();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '创建订单失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DraggableModal
      open={open}
      title="创建订单"
      okText="创建"
      cancelText="取消"
      confirmLoading={loading}
      onOk={handleOk}
      onCancel={onClose}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="studentId"
          label="学生"
          rules={[{ required: true, message: '请选择学生' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="选择学生"
            options={students.map((student) => ({
              value: student.id,
              label: `${student.name} · ${maskOrderMobile(student.guardianMobile)} · ${student.homeCampusName ?? ''}`,
            }))}
            data-testid="order-create-student-select"
          />
        </Form.Item>
        <Form.Item
          name="productId"
          label="课程商品包"
          rules={[{ required: true, message: '请选择在售课程商品包' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="选择在售课程商品包"
            options={products.map((product) => ({
              value: product.id,
              label: `${product.name} · ¥${(product.price / 100).toFixed(2)}`,
            }))}
            data-testid="order-create-product-select"
          />
        </Form.Item>
        {selectedStudent && (
          <Form.Item label="归属校区">
            <Select
              disabled
              value={selectedStudent.homeCampusId}
              options={campuses.map((c) => ({ label: c.name, value: c.id }))}
            />
          </Form.Item>
        )}
        {canDiscount && selectedProduct && (
          <>
            <Form.Item
              name="discountAmountYuan"
              label="减免金额（元）"
              rules={[
                {
                  validator: (_rule, value: number | undefined | null) => {
                    if (value === undefined || value === null || value === 0) {
                      return Promise.resolve();
                    }
                    if (value < 0) return Promise.reject(new Error('减免金额不能为负'));
                    if (selectedProduct && Math.round(value * 100) > selectedProduct.price) {
                      return Promise.reject(new Error('减免金额不能超过商品原始价格'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              {/* 不设 max：antd InputNumber 失焦会把超限值静默钳制到上限，
                  恰全额减免会造成误建 0 元单；超限交给下方 validator 明确报错拦截 */}
              <InputNumber
                min={0}
                precision={2}
                style={{ width: 200 }}
                placeholder="0 表示不减免"
                data-testid="order-create-discount-input"
              />
            </Form.Item>
            <Form.Item
              name="discountReason"
              label="减免原因"
              rules={[
                {
                  validator: (_rule, value: string | undefined) => {
                    if (discountMinor > 0 && !(value ?? '').trim()) {
                      return Promise.reject(new Error('减免大于 0 时必须填写原因'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.TextArea
                rows={2}
                maxLength={200}
                placeholder="减免大于 0 时必填"
                data-testid="order-create-discount-reason"
              />
            </Form.Item>
          </>
        )}
        {selectedProduct && (
          <Alert
            type="info"
            showIcon
            message={
              canDiscount && discountMinor > 0
                ? <>原价 <MoneyDisplay amountInCents={selectedProduct.price} /> − 减免 <MoneyDisplay amountInCents={discountMinor} /> = 应收总额 <MoneyDisplay amountInCents={receivableMinor} /></>
                : <>应收总额 <MoneyDisplay amountInCents={selectedProduct.price} /></>
            }
            description={
              <>
                {/* period-pass-admin-ui:SC-05：所选商品含期限卡 SKU 行时追加结清开通提示 */}
                {selectedProduct.skuConfigs.some((c) => c.chargeMode === 'PERIOD') && (
                  <div data-testid="order-create-period-tip">
                    含期限卡 SKU：结清后创建待激活期限卡，首次上课自动生效，有效期内不限次上课（不按课时开通）。
                  </div>
                )}
                <span>
                  {`首付款 ${(selectedProduct.depositRatio * 100).toFixed(0)}%；商品包含 ${selectedProduct.skuConfigs.length} 个 SKU，整包购买。`}
                </span>
              </>
            }
            data-testid="order-create-amount-alert"
          />
        )}
      </Form>
    </DraggableModal>
  );
}
